import React, { useEffect, useState } from "react";
import { Box, Typography, Grid, IconButton, Tooltip } from "@mui/material";
import ApiService from "../../services/api-service";
import Dialog from "@mui/material/Dialog";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import VideoPlayer from "../../components/VideoPlayer";
import VideoEdit from "../../components/VideoBox/VideoEdit";

export default function MyVideosTab({ userId }) {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editVideo, setEditVideo] = useState(null);

  // Fetch videos function so it can be reused after edit
  const fetchVideos = async () => {
    setLoading(true);
    try {
      const res = await ApiService.VIDEO_BY_UPLOADER(userId);
      setVideos(res.result || []);
    } catch (e) {
      setVideos([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (userId) fetchVideos();
    // eslint-disable-next-line
  }, [userId]);

  const handleOpen = (video) => {
    setSelectedVideo(video);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedVideo(null);
  };

  const handleEditOpen = (video, e) => {
    e.stopPropagation();
    setEditVideo(video);
    setEditOpen(true);
  };

  const handleEditClose = (refresh) => {
    setEditOpen(false);
    setEditVideo(null);
    if (refresh) fetchVideos();
  };

  return (
    <Box sx={{ color: "var(--text-primary, #fff)", mt: 4 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>My Videos</Typography>
      {loading ? (
        <Typography color="var(--text-primary, #fff)">Loading...</Typography>
      ) : videos.length === 0 ? (
        <Typography color="var(--text-primary, #fff)">No videos found.</Typography>
      ) : (
        <Grid container spacing={4}>
          {videos.map((video) => (
            <Grid item key={video._id} xs={12} sm={6} md={4} lg={2}>
              <Box
                sx={{
                  background: "var(--background-color, #181818)",
                  borderRadius: "16px",
                  boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.15)",
                  overflow: "hidden",
                  cursor: "pointer",
                  transition: "box-shadow 0.3s",
                  "&:hover": { boxShadow: "0 12px 40px 0 rgba(31,38,135,0.25)" },
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  p: 0,
                  position: "relative",
                }}
                onClick={() => handleOpen(video)}
              >
                {/* Edit Button */}
                <Tooltip title="Edit Video">
                  <IconButton
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      bgcolor: "var(--accent-color, #222)",
                      color: "var(--text-primary, #fff)",
                      "&:hover": { bgcolor: "var(--background-color, #333)" },
                      zIndex: 2,
                    }}
                    onClick={(e) => handleEditOpen(video, e)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {/* Main square image */}
                <Box
                  sx={{
                    width: "100%",
                    aspectRatio: "1 / 1",
                    background: "var(--accent-color, #222)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                  }}
                >
                  <img
                    src={video.thumbnail?.url || "/default-thumbnail.png"}
                    alt={video.title}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                  {video.isExplicit && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 12,
                        left: 12,
                        bgcolor: "rgba(255,0,0,0.7)",
                        px: 1.5,
                        py: 0.5,
                        borderRadius: "12px",
                        color: "var(--text-primary, #fff)",
                        fontSize: 12,
                        fontWeight: 600,
                        letterSpacing: 1,
                      }}
                    >
                      Explicit
                    </Box>
                  )}
                </Box>
                {/* Info section */}
                <Box sx={{ width: "100%", p: 2, background: "var(--background-color, #181818)" }}>
                  <Typography variant="h6" fontWeight={700} sx={{ color: "var(--text-primary, #fff)", mb: 0.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {video.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "var(--primary-color, #8fff8f)", fontWeight: 600 }}>
                    Duration: {video.duration ? video.duration.toFixed(2) + "s" : "N/A"}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "var(--text-secondary, #aaa)" }}>
                    Resolution: {video.resolution}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "var(--text-secondary, #aaa)", ml: 2 }}>
                    Views: {video.views}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#888", mt: 1, display: "block" }}>
                    {video.createdAt ? new Date(video.createdAt).toLocaleDateString() : ""}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Video Player Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <IconButton
          onClick={handleClose}
          sx={{ position: "absolute", top: 8, right: 8, color: "var(--text-primary, #fff)", zIndex: 2 }}
        >
          <CloseIcon />
        </IconButton>
        <Box sx={{ background: "var(--background-color, #000)", p: 3 }}>
          {selectedVideo && (
            <VideoPlayer video={selectedVideo.video.url} />
          )}
        </Box>
      </Dialog>

      {/* Video Edit Dialog */}
      <VideoEdit
        open={editOpen}
        handleClose={(e, close) => handleEditClose(!close)}
        item={editVideo}
        studioId={editVideo?.studioId}
      />
    </Box>
  );
}